import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Load GA script dynamically only after consent
export const loadGoogleAnalytics = (trackingId: string) => {
  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  // Load GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(...args: any[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', trackingId, {
    anonymize_ip: true, // Important for privacy
    cookie_flags: 'SameSite=Strict;Secure'
  });
};

// Sanitize sensitive paths before tracking
const sanitizePath = (path: string): string => {
  return path
    // Replace scene IDs with placeholder
    .replace(/\/scenes\/[a-zA-Z0-9-]+/, '/scenes/[id]')
    // Replace user IDs with placeholder
    .replace(/\/users\/[a-zA-Z0-9-]+/, '/users/[id]')
    // Remove email addresses
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
    // Remove query parameters (might contain tokens)
    .replace(/\?.*/, '')
    // Remove hash fragments
    .replace(/#.*/, '')
    // Replace any remaining long alphanumeric strings (likely IDs or tokens)
    .replace(/[a-f0-9]{16,}/gi, '[hash]');
};

// Custom hook for analytics
export function useAnalytics(trackingId: string) {
  const location = useLocation();

  useEffect(() => {
    // Check consent
    const consent = localStorage.getItem('analytics_consent');
    
    if (consent === 'accepted') {
      // Load GA if not already loaded
      loadGoogleAnalytics(trackingId);

      // Track page view with sanitized path
      if (window.gtag) {
        const sanitized = sanitizePath(location.pathname + location.search);
        window.gtag('config', trackingId, {
          page_path: sanitized,
          anonymize_ip: true
        });
        
        console.log('Analytics tracked:', sanitized); // for debugging
      }
    }
  }, [location, trackingId]);
}